class KiyohReviews extends HTMLElement {
    constructor() {
      super();
      this.template = this.querySelector('template');
      this.src = this.getAttribute('data-src');
      this.maxRetries = 3;
      this.retryDelay = 2000; // 2 seconds
    }
  
    connectedCallback() {
      if (!this.template || !this.src) {
        console.error('KiyohReviews: Missing template or data-src attribute');
        this.showError('Configuration error');
        return;
      }
      this.loadAndRender();
    }
  
    async loadAndRender() {
      let retryCount = 0;
      
      const tryLoad = async () => {
        try {
          this.showLoading();
          const res = await fetch(this.src);
          
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          
          const xmlText = await res.text();
          const parser = new DOMParser();
          const xml = parser.parseFromString(xmlText, 'application/xml');
  
          // Check for XML parsing errors
          const parserError = xml.querySelector('parsererror');
          if (parserError) {
            throw new Error('XML parsing error');
          }
  
          const summary = {
            avgRating: this.#getText(xml, 'averageRating'),
            totalReviews: this.#getText(xml, 'numberReviews'),
            percentageRecommendation: this.#getText(xml, 'percentageRecommendation'),
            companyName: this.#getText(xml, 'locationName'),
            viewReviewUrl: this.#getText(xml, 'viewReviewUrl')
          };
  
          const reviewNodes = Array.from(xml.querySelectorAll('reviews > reviews')).slice(0, 10);
          const reviews = reviewNodes.map((reviewEl) => {
            const author = this.#getText(reviewEl, 'reviewAuthor');
            const city = this.#getText(reviewEl, 'city');
            const date = this.#getText(reviewEl, 'dateSince')?.split('T')[0];
            const rating = this.#getText(reviewEl, 'rating');
            const contents = reviewEl.querySelectorAll('reviewContent > reviewContent');
  
            let oneliner = '';
            let opinion = '';
  
            contents.forEach(content => {
              const group = this.#getText(content, 'questionGroup');
              const value = this.#getText(content, 'rating');
  
              if (group === 'DEFAULT_ONELINER') oneliner = value;
              if (group === 'DEFAULT_OPINION') opinion = value;
            });
  
            return { author, city, date, rating, oneliner, opinion };
          });
  
          if (reviews.length === 0) {
            throw new Error('No reviews found');
          }
  
          this.#render(summary, reviews);
          this.hideLoading();
        } catch (error) {
          console.error('KiyohReviews error:', error);
          
          if (retryCount < this.maxRetries) {
            retryCount++;
            console.log(`Retrying (${retryCount}/${this.maxRetries})...`);
            setTimeout(tryLoad, this.retryDelay);
          } else {
            this.showError('Unable to load reviews at this time');
          }
        }
      };
  
      await tryLoad();
    }
  
    #getText(context, tag) {
      return context.querySelector(tag)?.textContent.trim() || '';
    }
  
    showLoading() {
      const slider = document.querySelector(`#Slider-${this.dataset.section}`);
      if (slider) {
        slider.innerHTML = `
          <div class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading reviews...</p>
          </div>
        `;
      }
    }
  
    hideLoading() {
      const loadingState = document.querySelector(`#Slider-${this.dataset.section} .loading-state`);
      if (loadingState) {
        loadingState.remove();
      }
    }
  
    showError(message) {
      this.hideLoading();
      const slider = document.querySelector(`#Slider-${this.dataset.section}`);
      if (slider) {
        slider.innerHTML = `
          <div class="error-state">
            <p>${message}</p>
          </div>
        `;
      }
    }
  
    #render(summary, reviews) {
      // Update section-title-left with summary information
      const sectionTitleLeft = document.querySelector('.section-title-left');
      if (sectionTitleLeft) {
        // Update stars rating
        const starValue = (parseFloat(summary.avgRating) / 2).toFixed(1); // Convert 10-point scale to 5-point scale
        const percentageStar = (starValue * 20).toFixed(0); // Convert to percentage
        
        const starsContainer = sectionTitleLeft.querySelector('.reviews-container');
        if (starsContainer) {
          const starSvg = starsContainer.querySelector('.rating');
          if (starSvg) {
            const ratingValue = starSvg.querySelector('.rating__value');
            if (ratingValue) {
              ratingValue.setAttribute('width', `${percentageStar}%`);
            }
          }
        }
  
        // Update the text next to the stars
        const starText = sectionTitleLeft.querySelector('.reviews-container span');
        if (starText) {
          starText.textContent = `${summary.avgRating}/10 (${summary.totalReviews} reviews)`;
        }
  
        // Update review link if available
        if (summary.viewReviewUrl) {
          const reviewLink = sectionTitleLeft.querySelector('[data-review-link]');
          if (reviewLink) {
            reviewLink.href = summary.viewReviewUrl;
            reviewLink.style.display = 'inline-block';
          }
        }
      }
  
      // Get the existing slider
      const slider = document.querySelector(`#Slider-${this.dataset.section}`);
      if (!slider) {
        console.error('Slider not found:', `#Slider-${this.dataset.section}`);
        return;
      }
  
      // Clear existing slides
      slider.innerHTML = '';
  
      // Add reviews as slides
      reviews.forEach((review, index) => {
        const { author, city, date, rating, oneliner, opinion } = review;
        const slide = document.createElement('div');
        slide.className = 'col-sm-8 col-xs-12 review-list__item slider__slide';
        slide.id = `Slide-${this.dataset.section}-${index}`;
        
        const reviewCard = document.createElement('div');
        reviewCard.className = 'review-card';
        
        // Add stars rating
        const reviewsContainer = document.createElement('div');
        reviewsContainer.className = 'reviews-container';
        const starValue = (parseFloat(rating) / 2).toFixed(1); // Convert 10-point scale to 5-point scale
        const percentageStar = (starValue * 20).toFixed(0); // Convert to percentage
        
        reviewsContainer.innerHTML = `
          <svg viewBox="0 0 1000 200" class="rating">
            <defs>
              <polygon id="star" points="100,0 131,66 200,76 150,128 162,200 100,166 38,200 50,128 0,76 69,66 "/>
              <clipPath id="stars">
                <use xlink:href="#star"/>
                <use xlink:href="#star" x="20%"/>
                <use xlink:href="#star" x="40%"/>
                <use xlink:href="#star" x="60%"/>
                <use xlink:href="#star" x="80%"/>
              </clipPath>
            </defs>
            <rect class='rating__background' clip-path="url(#stars)"></rect>
            <rect width="${percentageStar}%" class='rating__value' clip-path="url(#stars)"></rect>
          </svg>
        `;
        
        const reviewInfo = document.createElement('div');
        reviewInfo.className = 'review-card__info';
        reviewInfo.innerHTML = `
          <span class="name">${author} uit ${city}</span>
          <span class="description">
            <em>${oneliner}</em><br/>
            <p>${opinion}</p>
          </span>
        `;
        
        reviewCard.appendChild(reviewsContainer);
        reviewCard.appendChild(reviewInfo);
        slide.appendChild(reviewCard);
        slider.appendChild(slide);
      });
  
      // Reinitialize the slider
      const sliderComponent = slider.closest('slider-component');
      if (sliderComponent) {
        console.log('Reinitializing slider with', reviews.length, 'slides');
        // Force a reflow to ensure the slides are rendered
        slider.offsetHeight;
        // Reset the slider's state
        sliderComponent.sliderItems = sliderComponent.querySelectorAll('[id^="Slide-"]');
        sliderComponent.initPages();
      } else {
        console.error('Slider component not found');
      }
    }
  }
  
  customElements.define('kiyoh-reviews', KiyohReviews);